import {useCallback} from 'react';

import {FullDrawerConfirmation} from '@stripe-internal/conversations-ui';

import {closeConfirmationMessages as messages} from '../messages';
import {
  useApp,
  useConversation,
  useSetAppState,
  useShowCloseConfirmation,
  useShowRestartConfirmation,
} from '../hooks/appHooks';

type Props = {
  onClose?: () => void;
};

export function CloseConfirmationModal({onClose}: Props) {
  const app = useApp();
  const showCloseConfirmation = useShowCloseConfirmation();
  const showRestartConfirmation = useShowRestartConfirmation();
  const setAppState = useSetAppState();
  const {restartConversation} = useConversation();

  const cancelClose = useCallback(() => {
    setAppState((state) => {
      state.showCloseConfirmation = false;
    });
  }, [setAppState]);

  const confirmClose = useCallback(() => {
    if (onClose) {
      onClose();
      return;
    }
    app.close(true);
  }, [app, onClose]);

  const cancelRestart = useCallback(() => {
    setAppState((state) => {
      state.showRestartConfirmation = false;
      // Retain only triggerPoint so that any open intent doesn't persist in
      // state and get re-applied on subsequent visibility changes or conversation
      // starts. The user chose to keep the existing conversation.
      // todo(@danielxu): This block can be removed once we implement a proper
      // intent system, where intents are consumed once and automatically
      // discarded regardless of outcome.
      if (state.triggerParams) {
        state.triggerParams = {triggerPoint: state.triggerParams.triggerPoint};
      }
    });
  }, [setAppState]);

  const confirmRestart = useCallback(() => {
    setAppState((state) => {
      state.showRestartConfirmation = false;
    });
    restartConversation({});
  }, [setAppState, restartConversation]);

  if (showRestartConfirmation) {
    return (
      <FullDrawerConfirmation
        open
        title={messages.title}
        description={messages.description}
        confirmLabel={messages.restartConfirm}
        cancelLabel={messages.cancel}
        onConfirm={confirmRestart}
        onCancel={cancelRestart}
      />
    );
  }

  if (!showCloseConfirmation) {
    return null;
  }

  return (
    <FullDrawerConfirmation
      open
      title={messages.title}
      description={messages.description}
      confirmLabel={messages.confirm}
      cancelLabel={messages.cancel}
      onConfirm={confirmClose}
      onCancel={cancelClose}
    />
  );
}
